import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import {
  Form,
  Col,
  Card,
  Spin,
  Button,
  Input,
  message,
  Row,
  Typography,
  DatePicker,
  Modal,
} from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import Table from 'components/Table';
import Select from 'components/Select';
import FormSubmitControls from 'components/FormSubmitControls';
import { onError } from 'utils/handlers';
import { getTiposDeAlertas } from 'api/configuracion/tiposDeAlertas';
import { patchAlertas, postGenerarAlerta } from 'api/configuracion';
import { formatReceived, DATE_FRONTEND_FORMAT } from 'utils/formatValues';
import Uploader from 'components/Uploader';
import { getUsuario } from 'api/usuarios/usuarios';
import { toMoment } from 'utils/normalizers';
import { getContenTypePorTipoDePadron, gettersPadronesCajaMap } from 'utils/padrones';

const { Title } = Typography;

export const permissionConfig = {
  title: 'Alertas',
  model: 'alerta',
  catalogs: ['padron', 'contenttype'],
};

const renderUser = (e) => `${e?.email || ''} - ${e?.first_name || ''} ${e?.last_name || ''} ${e?.second_last_name || ''}`;

const Alertas = ({
  permission,
  padron,
  disabled,
  tipoDePadron,
  callback,
  tramite,
}) => {
  const { user } = useSelector((state) => state.auth);
  const { estadosGlobales } = useSelector((state) => state.catalogs);
  const [formAlerta] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [visibleForm, setVisibleForm] = useState(false);
  const [alertas, setAlertas] = useState([]);
  const [tiposDeAlertas, setTiposDeAlertas] = useState([]);
  const [visibleCancel, setVisibleCancel] = useState(false);
  const [usuarios, setUsuarios] = useState([]);
  const [formCancel] = Form.useForm();

  const fetchUsuarios = async (_alertas) => {
    const promises = _alertas.filter((e) => e.usuario_cancelo)
      .map((e) => getUsuario(e.usuario_cancelo));
    const _usuarios = await Promise.all(promises);
    setUsuarios(_usuarios);
  };

  const fetchAlertas = async () => {
    setLoading(true);
    const [_padron] = await gettersPadronesCajaMap[tipoDePadron]({ id: padron.id });
    await fetchUsuarios(_padron.alerta || []);
    _padron.alerta = formatReceived(_padron.alerta || []);
    setAlertas(_padron.alerta || []);
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      const _tiposDeAlertas = await getTiposDeAlertas();
      if (mounted) {
        setTiposDeAlertas(_tiposDeAlertas.filter((a) => [tipoDePadron, 5].includes(a.padron)));
        await fetchAlertas();
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
  }, []);

  const columns = [
    {
      titleText: 'Folio',
      dataIndex: 'folio',
      key: 'folio',
      width: 200,
    },
    {
      titleText: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      width: 400,
    },
    {
      titleText: 'Tipo de Alerta',
      dataIndex: 'tipo_de_alerta',
      key: 'tipo_de_alerta',
      width: 400,
      render: (tipo_de_alerta) => tipo_de_alerta?.descripcion,
    },
    {
      titleText: 'Fecha de Cierre',
      dataIndex: 'fecha_de_cierre',
      key: 'fecha_de_cierre',
      width: 200,
      render: (v) => (v?.format('DD/MM/YYYY')),
    },
    {
      titleText: 'Estado',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 100,
      render: (e) => estadosGlobales.find((i) => i.id === e)?.descripcion,
    },
    {
      titleText: 'Usuario que canceló',
      dataIndex: 'usuario_cancelo',
      key: 'usuario_cancelo',
      width: 500,
      render: (e) => {
        const usuario = usuarios.find((u) => u.id === e);
        return usuario ? renderUser(usuario) : '';
      },
    },
    {
      titleText: 'Fecha de cancelación',
      dataIndex: 'fecha_de_cancelacion',
      key: 'fecha_de_cancelacion',
      width: 200,
      render: (v) => (v?.format('DD/MM/YYYY')),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };
  const rules = { required: [{ required: true }] };

  const handleOnRowClick = (record) => setSelectedRowKeys([record.id]);

  const onCancelAlerta = () => {
    formAlerta.resetFields();
    setVisibleForm(false);
    setSelectedRowKeys([]);
  };

  const onFinishAlerta = async () => {
    try {
      setLoading(true);
      await formAlerta.validateFields();
      const values = formAlerta.getFieldsValue();
      values.content_type = getContenTypePorTipoDePadron(tipoDePadron);
      const match = tiposDeAlertas.find((e) => e.id === values.tipo_de_alerta);
      if (match?.documento) delete match.documento;
      if (match?.observaciones) delete match.observaciones;
      const allValues = {
        ...match,
        ...values,
        es_restrictiva: !match.es_informativa,
        object_id: padron?.id,
      };
      const created = await postGenerarAlerta(allValues, {
        clave: tramite?.folio,
        idPadron: padron?.id,
        accionInf: tramite ? 'CREAR_ALERTAS_EN_TRAMITE' : undefined,
      });
      if (created) {
        onCancelAlerta();
        await callback(`Alerta ${created.folio} generada`);
        await fetchAlertas();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const onClickAdd = () => {
    if (padron?.id) {
      onCancelAlerta();
      setVisibleForm(true);
    }
  };

  const handleCancel = async () => {
    try {
      setLoading(true);
      await formCancel.validateFields();
      const {
        motivo_de_la_cancelacion,
        documento,
        usuario_cancelo,
        fecha_de_cancelacion,
      } = formCancel.getFieldsValue();
      const [id] = selectedRowKeys;
      const match = alertas.find((a) => a.id === id);
      const canceled = await patchAlertas(id, {
        motivo_de_la_cancelacion,
        estados_globales: 5,
        documento,
        usuario_cancelo,
        fecha_de_cancelacion,
      }, {
        clave: tramite?.folio,
        idPadron: padron?.id,
        accionInf: tramite ? 'EDITAR_ALERTAS_EN_TRAMITE' : undefined,
      });
      if (canceled) {
        await callback(`Alerta ${match.folio} cancelada`);
        setVisibleCancel(false);
        formCancel.resetFields();
        setTimeout(() => fetchAlertas(padron), 1000);
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  return (
    <Row align="center" justify="center" className="container">
      <Spin tip="Cargando..." spinning={loading}>

        {!visibleForm && (
          <>
            <Title level={4}>
              Alertas
            </Title>
            <Table
              cols={columns}
              data={alertas}
              rowSelection={rowSelection}
              handleOnRowClick={handleOnRowClick}
              controls={{ onClickAdd }}
              rowClassName={(record) => {
                let classNames = '';
                if (record.estados_globales === 5) {
                  classNames = 'txtdec-line-throught';
                }
                return classNames;
              }}
              customActions={[
                {
                  text: 'Cancelar Alerta',
                  className: 'danger',
                  disabledBySelection: true,
                  onClick: () => {
                    const [key] = selectedRowKeys;
                    const match = alertas.find((e) => e.id === key);
                    if (match?.estados_globales === 5) {
                      message.warn('La alerta ya está cancelada.');
                      return;
                    }
                    if (key) {
                      setVisibleCancel(true);
                    } else {
                      message.warn('Se necesita seleccionar una alerta.');
                    }
                  },
                  icon: <DeleteOutlined />,
                },
              ]}
              allowSearch={false}
              rowKey="id"
              noControls={disabled}
              permission={permission}
            />
          </>
        )}
        {visibleForm && (
          <Card
            className="form-container"
            title={`${!selectedRowKeys.length ? 'Agregar ' : ''}Alerta`}
            bordered
            extra={(
              <FormSubmitControls
                onFinish={() => formAlerta.submit()}
                onCancel={onCancelAlerta}
              />
          )}
          >
            <Form
              form={formAlerta}
              layout="vertical"
              onFinish={() => onFinishAlerta()}
              scrollToFirstError
              initialValues={{
                fecha_de_registro_de_alerta: toMoment(new Date()),
              }}
            >
              <Row gutter={10}>
                <Col xs={24} sm={24} md={12} lg={8}>
                  <Form.Item
                    name="tipo_de_alerta"
                    label="Alerta"
                    hasFeedback
                    rules={rules.required}
                  >
                    <Select dataSource={tiposDeAlertas} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={12} lg={8}>
                  <Form.Item
                    name="fecha_de_cierre"
                    label="Fecha de Cierre"
                  >
                    <DatePicker format={DATE_FRONTEND_FORMAT} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={12} lg={8}>
                  <Form.Item
                    name="fecha_de_registro_de_alerta"
                    label="Fecha de Registro"
                    rules={rules.required}
                  >
                    <DatePicker format={DATE_FRONTEND_FORMAT} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={12} lg={8}>
                  <Uploader
                    title="Documento"
                    limitMB={50}
                    fullSize
                    formItemName="documento"
                    allowedExtensions={['pdf', 'docx', 'doc']}
                    rules={rules.documento}
                    allowPreview={false}
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={24}>
                  <Form.Item
                    name="descripcion"
                    rules={rules.required}
                    label="Descripción"
                  >
                    <Input
                      autoSize={{ minRows: 3, maxRows: 3 }}
                      allowClear
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={12}>
                  <Form.Item
                    name="observaciones"
                    rules={rules.required}
                    label="Observaciones"
                  >
                    <Input.TextArea
                      autoSize={{ minRows: 3, maxRows: 3 }}
                      allowClear
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={12}>
                  <Form.Item
                    name="observaciones_internas"
                    label="Observaciones internas"
                  >
                    <Input.TextArea
                      autoSize={{ minRows: 3, maxRows: 3 }}
                      allowClear
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item hidden>
                <Button htmlType="submit" />
              </Form.Item>
            </Form>
          </Card>
        )}
      </Spin>
      <Modal
        open={visibleCancel}
        closable={false}
        footer={null}
        centered
        title={(
          <FormSubmitControls
            onCancel={() => {
              setVisibleCancel(false);
              formCancel.resetFields();
            }}
            onFinish={() => handleCancel()}
            label="Cancelar Alerta"
          />
        )}
      >
        <Form
          layout="vertical"
          form={formCancel}
          onFinish={() => handleCancel()}
          scrollToFirstError
          initialValues={{
            fecha_de_cancelacion: toMoment(new Date()),
            usuario_cancelo: user?.id,
          }}
        >
          <Row>
            <Col span={24}>
              <Form.Item
                label="Motivo de cancelación"
                name="motivo_de_la_cancelacion"
                rules={rules.required}
              >
                <Input.TextArea autoSize={{ minRows: 4, maxRows: 4 }} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                label="Fecha de cancelación"
                name="fecha_de_cancelacion"
              >
                <DatePicker format={DATE_FRONTEND_FORMAT} disabled />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                label="usuario que canceló"
                name="usuario_cancelo"
              >
                <Select
                  dataSource={[user]}
                  disabled
                  render={renderUser}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Uploader
                title="Documento"
                limitMB={50}
                fullSize
                formItemName="documento"
                allowedExtensions={['pdf', 'docx', 'doc']}
                rules={rules.documento}
                allowPreview={false}
              />
            </Col>
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Row>
        </Form>
      </Modal>
    </Row>
  );
};

Alertas.propTypes = {
  permission: PropTypes.shape().isRequired,
  padron: PropTypes.shape().isRequired,
  disabled: PropTypes.bool,
  tipoDePadron: PropTypes.number.isRequired,
  callback: PropTypes.func.isRequired,
  tramite: PropTypes.shape(),
};

Alertas.defaultProps = {
  disabled: false,
  tramite: null,
};

export default Alertas;
